const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080'
const CHARTS_BASE = import.meta.env.VITE_CHARTS_URL || 'http://localhost:8081'

function getToken(): string {
  return localStorage.getItem('proxima_token') || ''
}

export function setToken(token: string) {
  localStorage.setItem('proxima_token', token)
}

export function clearToken() {
  localStorage.removeItem('proxima_token')
}

export function isAuthenticated(): boolean {
  return !!getToken()
}

async function request<T>(base: string, path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...options.headers,
    },
  })
  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || res.statusText)
  }
  return res.json()
}

function indexer<T>(path: string, options: RequestInit = {}): Promise<T> {
  return request(API_BASE, path, options)
}

function charts<T>(path: string, options: RequestInit = {}): Promise<T> {
  return request(CHARTS_BASE, path, options)
}

export const api = {
  login: (password: string) => indexer<{ token: string }>('/api/auth', { method: 'POST', body: JSON.stringify({ password }) }),
  getTokens: () => indexer<any[]>('/api/tokens'),
  addToken: (mint: string, symbol?: string) => indexer<any>('/api/tokens', { method: 'POST', body: JSON.stringify({ mint, symbol }) }),
  removeToken: (mint: string) => indexer<any>(`/api/tokens/${mint}`, { method: 'DELETE' }),
  getImportStatus: () => indexer<Record<string, { total: number; imported: number; status: string; error: string | null }>>('/api/tokens/import-status'),
  getGrpc: () => indexer<any[]>('/api/grpc'),
  addGrpc: (data: { url: string; token?: string; max_connections?: number; max_age_secs?: number }) =>
    indexer<any>('/api/grpc', { method: 'POST', body: JSON.stringify(data) }),
  updateGrpc: (id: number, data: { max_connections?: number; max_age_secs?: number; enabled?: boolean }) =>
    indexer<any>(`/api/grpc/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGrpc: (id: number) => indexer<any>(`/api/grpc/${id}`, { method: 'DELETE' }),
  killConnection: (endpointId: number, connId: number) =>
    indexer<any>(`/api/grpc/${endpointId}/kill/${connId}`, { method: 'POST' }),
  spawnConnection: (endpointId: number) => indexer<any>(`/api/grpc/${endpointId}/spawn`, { method: 'POST' }),
  getRpc: () => indexer<any[]>('/api/rpc'),
  addRpc: (data: { url: string; token?: string }) =>
    indexer<any>('/api/rpc', { method: 'POST', body: JSON.stringify(data) }),
  updateRpc: (id: number, data: { enabled?: boolean }) =>
    indexer<any>(`/api/rpc/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRpc: (id: number) => indexer<any>(`/api/rpc/${id}`, { method: 'DELETE' }),
  getStats: () => indexer<any>('/api/stats'),
  getTokenMetadata: (mint: string) => indexer<any>(`/api/tokens/${mint}/metadata`),
  refetchMetadata: (mint: string) => indexer<any>(`/api/tokens/${mint}/refetch`, { method: 'POST' }),

  getTransactions: (params?: { mint?: string; per_page?: number; cursor?: string; sort_by?: string; sort_order?: string }) => {
    const q = new URLSearchParams()
    if (params?.mint) q.set('mint', params.mint)
    if (params?.per_page) q.set('per_page', String(params.per_page))
    if (params?.cursor) q.set('cursor', params.cursor)
    if (params?.sort_by) q.set('sort_by', params.sort_by)
    if (params?.sort_order) q.set('sort_order', params.sort_order)
    return charts<{ data: any[]; total: number; per_page: number; next_cursor: string | null }>(`/api/transactions?${q}`)
  },
  getTransactionsHistory: (params?: { per_page?: number; cursor?: string; search?: string; mint?: string }) => {
    const q = new URLSearchParams()
    if (params?.per_page) q.set('per_page', String(params.per_page))
    if (params?.cursor) q.set('cursor', params.cursor)
    if (params?.search) q.set('search', params.search)
    if (params?.mint) q.set('mint', params.mint)
    return charts<{ data: any[]; total: number; per_page: number; next_cursor: string | null }>(`/api/transactions/history?${q}`)
  },
  getHolders: (params: { mint: string; per_page?: number; cursor?: string; sort_by?: string; sort_order?: string; addr?: string }) => {
    const q = new URLSearchParams({ mint: params.mint })
    if (params.per_page) q.set('per_page', String(params.per_page))
    if (params.cursor) q.set('cursor', params.cursor)
    if (params.sort_by) q.set('sort_by', params.sort_by)
    if (params.sort_order) q.set('sort_order', params.sort_order)
    if (params.addr) q.set('addr', params.addr)
    return charts<{ data: any[]; total: number; per_page: number; next_cursor: string | null }>(`/api/holders?${q}`)
  },
  getSymbolInfo: (mint: string) => charts<any>(`/api/symbol?mint=${mint}`),
  getCentrifugoToken: () => indexer<{ token: string }>('/api/centrifugo/token'),
  centrifugoSubscribe: (channel: string) => indexer<any>('/api/centrifugo/subscribe', { method: 'POST', body: JSON.stringify({ channel }) }),
}
